export const formatWallMs = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return '';
  return ms >= 10000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
};

// API datetimes are "YYYY-MM-DD HH:MM:SS" in UTC.
export const formatDuration = (
  startedAt: string | null,
  finishedAt: string | null,
  now: () => number = Date.now
): string => {
  if (!startedAt) return '';
  const start = Date.parse(`${startedAt.replace(' ', 'T')}Z`);
  const end = finishedAt ? Date.parse(`${finishedAt.replace(' ', 'T')}Z`) : now();
  if (Number.isNaN(start) || Number.isNaN(end)) return '';
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} m ${seconds % 60} s`;
};

// A span already measured in seconds — engine drive time, planned route length.
export const formatSeconds = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '';
  const whole = Math.max(0, Math.round(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  if (hours) return `${hours} h ${minutes} m`;
  if (minutes) return `${minutes} m`;
  return `${whole} s`;
};

// How long a planned route occupies the driver: first planned pickup to last planned dropoff.
// Both ends are API datetimes ("YYYY-MM-DD HH:MM:SS", UTC).
export const plannedSpanSeconds = (
  plannedStartAt: string | null | undefined,
  plannedEndAt: string | null | undefined
): number | null => {
  if (!plannedStartAt || !plannedEndAt) return null;
  const start = Date.parse(`${plannedStartAt.replace(' ', 'T')}Z`);
  const end = Date.parse(`${plannedEndAt.replace(' ', 'T')}Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  return Math.round((end - start) / 1000);
};

/**
 * What a planned route earns its driver. `counted` is how many of the plan's rides recorded an
 * amount: plans written before `amount` existed have none, and a zero total would read as "this
 * route earns nothing" rather than "not recorded", so the caller shows the total only when
 * something was counted and says so when only part of the plan was.
 */
export const planEarnings = (
  plan: { amount?: number | null }[] | null | undefined
): { total: number; counted: number; rides: number } => {
  const amounts = (plan ?? [])
    .map(ride => ride.amount)
    .filter((value): value is number => value != null);
  return {
    total: amounts.reduce((sum, value) => sum + value, 0),
    counted: amounts.length,
    rides: (plan ?? []).length
  };
};

// en-CA renders YYYY-MM-DD, and Intl handles the DST offset that makes 2026-08-24 00:00 UTC
// still the 23rd in New York.
const NEW_YORK_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' });

// Which New York calendar day a UTC API datetime falls on — the day a run plans, so the day a
// route or ride belongs to.
export const newYorkDay = (utcDatetime: string | null | undefined): string => {
  if (!utcDatetime) return '';
  const ms = Date.parse(`${utcDatetime.replace(' ', 'T')}Z`);
  if (Number.isNaN(ms)) return '';
  return NEW_YORK_DAY.format(ms);
};

// Today where the planning happens, which is not today where the browser happens to be: after
// 20:00 in New York it is already tomorrow in UTC, and a UTC-derived default would skip a day.
export const todayInNewYork = (now: () => number = Date.now): string => NEW_YORK_DAY.format(now());

/** `days` before today in New York, as YYYY-MM-DD. */
export const newYorkDaysAgo = (days: number, now: () => number = Date.now): string =>
  NEW_YORK_DAY.format(now() - days * 86_400_000);

export const plannedDay = (fromDatetime: string | null | undefined): string =>
  (fromDatetime ?? '').slice(0, 10);

export const parseIdList = (raw: string): number[] =>
  raw
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
    .map(Number)
    .filter(value => Number.isFinite(value));

/**
 * What a fleet is called on screen. A registered name wins; fleet 0 is the routes carrying no
 * fleet — a Myle driver's own — and an unnamed fleet falls back to its id, the same three rules
 * the API's own fleet labels follow. The id travels with the name because a run's scope, its
 * groups and the API are all by id.
 */
/**
 * What the server plans for a run, a world or a config call that names no tenant. A run recorded
 * before the tenant marker existed reads as this one, and so does the tenant it would be
 * re-planned for.
 */
export const DEFAULT_TENANT = 'default';

// The router's reasons for sitting a driver out, said the way the list reads them.
const SKIP_REASONS: Record<string, string> = {
  FIRST_TRIP_TOO_LATE: 'first trip too late',
  ROUTING_DISABLED: 'routing off',
  DRIVER_NOT_ELIGIBLE: 'driver not eligible'
};

/**
 * Why a skipped route was skipped, or null when the route was not skipped or did not say. A
 * skipped route carries its reason in `error_code`, which on any other outcome is a real error.
 */
export const skipReason = (route: {
  outcome: string | null;
  error_code: string | null;
}): string | null => {
  if (route.outcome !== 'skipped' || !route.error_code) return null;
  return SKIP_REASONS[route.error_code] ?? route.error_code.toLowerCase().replace(/_/g, ' ');
};

export const MYLE_FLEET = 'Myle';
export const NO_FLEET_ID = 0;

export const namesById = (entries: { id: number; name: string }[]): Map<number, string> =>
  new Map(entries.map(entry => [entry.id, entry.name]));

export const namedFleet = (id: number, names: Map<number, string>): string => {
  const name = names.get(id);
  if (name) return `${name} (${id})`;
  return id === NO_FLEET_ID ? `${MYLE_FLEET} (${id})` : `Fleet ${id}`;
};

/** Who drove a run's route: name and id when the driver was registered, the id alone otherwise. */
export const driverLabel = (route: { driver_id: number | null; driver_name: string | null }): string => {
  if (route.driver_id === null) return '—';
  return route.driver_name ? `${route.driver_name} (${route.driver_id})` : `Driver ${route.driver_id}`;
};

export const namedCompany = (id: number, names: Map<number, string>): string => {
  const name = names.get(id);
  return name ? `${name} (${id})` : `Company ${id}`;
};
